// The schedule week's weather row (legacy composables/useWeather.ts and ScheduleCalendar.vue's day-meta; Andrew,
// 2026-10-14: build it as legacy has it). The browser asks Open-Meteo, no key: the location's city is geocoded, then
// each day's high (°F) and WMO weather code come from the 16-day forecast, and the shown week's past days from the
// archive. Any failure leaves the row out silently, as legacy does. Only where the location has a city and a state.
// Three legacy bugs are fixed here (field-notes/bugs.md):
//   - B348 / L506: legacy asked for every location's days in America/Los_Angeles and judged "past" by the browser's
//     clock; here the location's own zone and business date are used.
//   - B349 / L507: legacy kept the first city's coordinates for the whole visit, so after switching locations the
//     schedule showed the first city's weather; here each place is geocoded (and cached) on its own.
//   - B350 / L508: legacy geocoded the city alone and took the first match (Portland, Maine got Portland, Oregon's
//     weather); here the match in the location's state wins, and legacy's first match is the fallback.
import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, type LocalDate } from '@radish/core/time';

export interface DayWeather {
  readonly date: string;
  /** The day's high, °F, rounded as legacy rounds it (`Math.round`). */
  readonly tempMax: number;
  readonly weatherCode: number;
  /** Legacy's Material icon name for the code (the page draws its lucide equivalent). */
  readonly icon: WeatherIcon;
  readonly label: string;
}

export type WeatherIcon = 'wb_sunny' | 'wb_cloudy' | 'cloud' | 'grain' | 'water_drop' | 'ac_unit' | 'thunderstorm';

/** Legacy's WMO table, verbatim (weatherCodeToMeta). */
export function weatherCodeToMeta(code: number): { readonly icon: WeatherIcon; readonly label: string } {
  if (code === 0) return { icon: 'wb_sunny', label: 'Clear sky' };
  if (code === 1) return { icon: 'wb_cloudy', label: 'Mostly clear' };
  if (code === 2) return { icon: 'wb_cloudy', label: 'Partly cloudy' };
  if (code === 3) return { icon: 'cloud', label: 'Overcast' };
  if (code <= 49) return { icon: 'cloud', label: 'Foggy' };
  if (code === 51) return { icon: 'grain', label: 'Light drizzle' };
  if (code === 53) return { icon: 'grain', label: 'Drizzle' };
  if (code === 55) return { icon: 'grain', label: 'Heavy drizzle' };
  if (code <= 59) return { icon: 'grain', label: 'Freezing drizzle' };
  if (code === 61) return { icon: 'water_drop', label: 'Light rain' };
  if (code === 63) return { icon: 'water_drop', label: 'Rain' };
  if (code === 65) return { icon: 'water_drop', label: 'Heavy rain' };
  if (code <= 69) return { icon: 'water_drop', label: 'Freezing rain' };
  if (code === 71) return { icon: 'ac_unit', label: 'Light snow' };
  if (code === 73) return { icon: 'ac_unit', label: 'Snow' };
  if (code === 75) return { icon: 'ac_unit', label: 'Heavy snow' };
  if (code === 77) return { icon: 'ac_unit', label: 'Snow grains' };
  if (code <= 82) return { icon: 'water_drop', label: 'Rain showers' };
  if (code <= 86) return { icon: 'ac_unit', label: 'Snow showers' };
  if (code === 95) return { icon: 'thunderstorm', label: 'Thunderstorm' };
  if (code <= 99) return { icon: 'thunderstorm', label: 'Thunderstorm with hail' };
  return { icon: 'cloud', label: 'Cloudy' };
}

/** Legacy's tooltip: "Drizzle · 68°F". */
export const weatherTooltip = (day: DayWeather): string => `${day.label} · ${String(day.tempMax)}°F`;

/** Open-Meteo's daily block, legacy's parseDailyResponse. A day without a high is left out rather than shown as 0°. */
export function parseDaily(json: unknown): DayWeather[] {
  const daily = (json as { daily?: { time?: string[]; temperature_2m_max?: (number | null)[]; weathercode?: (number | null)[] } } | null)?.daily;
  if (daily?.time === undefined) return [];
  return daily.time.flatMap((date, i) => {
    const high = daily.temperature_2m_max?.[i];
    const code = daily.weathercode?.[i];
    if (typeof high !== 'number' || typeof code !== 'number') return [];
    return [{ date, tempMax: Math.round(high), weatherCode: code, ...weatherCodeToMeta(code) }];
  });
}

const US_STATES: Readonly<Record<string, string>> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
  DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico',
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick', NL: 'Newfoundland and Labrador', NS: 'Nova Scotia',
  NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon',
};

/** The state as Open-Meteo names it (`admin1`): a two-letter code is spelled out, a full name is kept. */
const stateName = (state: string): string => {
  const t = state.trim();
  return (US_STATES[t.toUpperCase()] ?? t).toLowerCase();
};

/** Legacy asks for one match; ten let the state choose among same-named places (B350). */
const geocodeUrl = (city: string): string =>
  `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city.trim())}&count=10&language=en&format=json`;

interface Place { readonly lat: number; readonly lng: number }

/** The first match in the location's state; legacy's first match when none is (B350). */
export function pickPlace(json: unknown, state: string): Place | null {
  const results = (json as { results?: { latitude: number; longitude: number; admin1?: string }[] } | null)?.results ?? [];
  const wanted = stateName(state);
  const hit = results.find((r) => (r.admin1 ?? '').toLowerCase() === wanted) ?? results[0];
  return hit === undefined ? null : { lat: hit.latitude, lng: hit.longitude };
}

const forecastUrl = (p: Place, timezone: string): string =>
  `https://api.open-meteo.com/v1/forecast?latitude=${String(p.lat)}&longitude=${String(p.lng)}&daily=temperature_2m_max,weathercode&temperature_unit=fahrenheit&timezone=${encodeURIComponent(timezone)}&forecast_days=16`;

const archiveUrl = (p: Place, start: string, end: string, timezone: string): string =>
  `https://archive-api.open-meteo.com/v1/archive?latitude=${String(p.lat)}&longitude=${String(p.lng)}&start_date=${start}&end_date=${end}&daily=temperature_2m_max,weathercode&temperature_unit=fahrenheit&timezone=${encodeURIComponent(timezone)}`;

/**
 * The archive range for a week, legacy's rule: only a week that starts before today, from its first day to yesterday
 * (the location's business date). Legacy ran it to yesterday whatever the week; it stops at the week's last day here.
 */
export function historyRange(weekStart: string, today: string): { readonly start: string; readonly end: string } | null {
  if (!(weekStart < today)) return null;
  const yesterday = addDays(today as LocalDate, -1) as string;
  const weekEnd = addDays(weekStart as LocalDate, 6) as string;
  const end = yesterday < weekEnd ? yesterday : weekEnd;
  return weekStart <= end ? { start: weekStart, end } : null;
}

/** The fetch the weather row calls. The app passes the browser's; the tests pass a stand-in, so no test reaches Open-Meteo. */
export type WeatherFetch = (url: string) => Promise<{ readonly ok: boolean; json(): Promise<unknown> }>;

const browserFetch: WeatherFetch = (url) => globalThis.fetch(url);
const WeatherFetchContext = createContext<WeatherFetch>(browserFetch);
export const WeatherFetchProvider = WeatherFetchContext.Provider;

const getJson = async (fetcher: WeatherFetch, url: string): Promise<unknown> => {
  const res = await fetcher(url);
  if (!res.ok) throw new Error(`Open-Meteo ${String(url)} failed`);
  return res.json();
};

// Fetched once per place (and per week for the archive) for the visit, like legacy's fetchedRanges; failures stay silent.
const once = { staleTime: Number.POSITIVE_INFINITY, gcTime: Number.POSITIVE_INFINITY, retry: false, refetchOnWindowFocus: false } as const;

/** The week's weather by date: an empty map until (or unless) Open-Meteo answers. */
export function useWeekWeather(address: { readonly city: string; readonly state: string } | null, timezone: string, weekStart: string, today: string): ReadonlyMap<string, DayWeather> {
  const fetcher = useContext(WeatherFetchContext);
  const city = address?.city.trim() ?? '';
  const state = address?.state.trim() ?? '';
  const place = useQuery({
    ...once,
    queryKey: ['open-meteo', 'place', city, state],
    queryFn: async () => pickPlace(await getJson(fetcher, geocodeUrl(city)), state),
    enabled: city !== '' && state !== '',
  });
  const p = place.data ?? null;
  const forecast = useQuery({
    ...once,
    queryKey: ['open-meteo', 'forecast', p?.lat, p?.lng, timezone],
    queryFn: async () => parseDaily(await getJson(fetcher, forecastUrl(p as Place, timezone))),
    enabled: p !== null,
  });
  const range = historyRange(weekStart, today);
  const history = useQuery({
    ...once,
    queryKey: ['open-meteo', 'archive', p?.lat, p?.lng, timezone, range?.start, range?.end],
    queryFn: async () => parseDaily(await getJson(fetcher, archiveUrl(p as Place, (range as { start: string }).start, (range as { end: string }).end, timezone))),
    enabled: p !== null && range !== null && forecast.isFetched,
  });
  const byDate = new Map<string, DayWeather>();
  for (const d of forecast.data ?? []) byDate.set(d.date, d);
  for (const d of history.data ?? []) byDate.set(d.date, d);
  return byDate;
}
